
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, ArrowRight, RefreshCw, Gavel, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LoginAccount } from '../../services/authService';
import './OTPVerification.css';
import adminAuthService from '../../services/adminAuthService';
import TokenExpirationService from '../../services/tokenExpirationService';
import ErrorModal from '../Common/ErrorModal';
import SuccessModal from '../Common/SuccessModal';
import InfoModal from '../Common/InfoModal';

const OTPVerification: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [otp, setOtp] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const { verifyOTP, selectAccount, login } = useAuth();
  // Set when the verified email belongs to several accounts
  const [accounts, setAccounts] = useState<LoginAccount[] | null>(null);
  const navigate = useNavigate();
  
  // Modal states
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });
  const [successModal, setSuccessModal] = useState({ isOpen: false, message: '' });
  const [infoModal, setInfoModal] = useState({ isOpen: false, message: '' });

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setOtp(value);
  };

  useEffect(() => {
    const pendingEmail = localStorage.getItem('pendingEmail');
    if (!pendingEmail) { navigate('/login'); return; }

    const timer = setInterval(() => {
      setCountdown(p => {
        if (p <= 1) { setCanResend(true); return 0; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  const finishLogin = () => {
    const user = JSON.parse(localStorage.getItem('auctionUser') || '{}');

    // Show success modal before redirecting
    setIsLoading(false);
    setSuccessModal({ isOpen: true, message: 'OTP Verified Successfully! Login Successful.' });

    // Navigate after showing success message
    setTimeout(() => {
      navigate(user?.role === 'admin' ? '/admin?welcome=true' : '/dashboard?welcome=true', { replace: true });
    }, 2000);
  };

  const handleSelectAccount = async (account: LoginAccount) => {
    setIsLoading(true);
    try {
      await selectAccount(account.id);
      finishLogin();
    } catch (err: any) {
      setIsLoading(false);
      setErrorModal({ isOpen: true, message: err?.message || 'Could not log in to that account. Please try again.' });
    }
  };

  const roleLabel = (role: string) =>
    role === 'channel_partner' ? 'Channel Partner' : role === 'auction_viewer' ? 'Auction Viewer' : 'User';

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!otp.trim()) { 
      const msg = 'Please enter OTP to continue';
      setTimeout(() => setErrorModal({ isOpen: true, message: msg }), 100);
      return; 
    }
    if (!/^\d{4}$/.test(otp)) { 
      const msg = 'OTP must be exactly 4 digits. Please check and try again.';
      setTimeout(() => setErrorModal({ isOpen: true, message: msg }), 100);
      return; 
    }

    const isAdmin = localStorage.getItem('isAdminLogin') === 'true';
    const email = localStorage.getItem('pendingEmail');

    setIsLoading(true);
    
    const verifyOTPAsync = async () => {
      try {
      if (isAdmin) {
        // Admin OTP verification
        const adminSessionId = localStorage.getItem('adminSessionId');
        if (!adminSessionId) {
          throw new Error('Session expired. Please request OTP again.');
        }

        const response = await adminAuthService.verifyAdminOTP({
          email: email || '',
          otp,
          sessionId: adminSessionId
        });

        if (response.success && response.admin && response.token) {
          const admin = {
            id: response.admin.id.toString(),
            phoneNumber: response.admin.phone ? '+91' + response.admin.phone : '',
            role: 'admin' as const,
            name: response.admin.name,
            personName: response.admin.name,
            companyName: 'Auction Admin',
            companyAddress: 'Admin Office',
            email: response.admin.email,
            mailId: response.admin.email,
            isVerified: true,
          };
          
          localStorage.setItem('auctionUser', JSON.stringify(admin));
          localStorage.setItem('authToken', response.token);
          
          // Set login timestamp for 3-hour session expiration
          TokenExpirationService.setLoginTimestamp();
          
          localStorage.removeItem('pendingPhoneNumber');
          localStorage.removeItem('pendingEmail');
          localStorage.removeItem('pendingOtpEmail');
          localStorage.removeItem('isAdminLogin');
          localStorage.removeItem('adminSessionId');
          
          // Show success modal before redirecting
          setIsLoading(false);
          const successMsg = 'OTP Verified Successfully! Admin Login Successful.';
          setSuccessModal({ isOpen: true, message: successMsg });
          
          // Redirect after showing success message
          setTimeout(() => {
            window.location.href = '/admin?welcome=true';
          }, 2000);
        } else {
          throw new Error(response.message || 'Invalid OTP. Please check and try again.');
        }
      } else {
        // Regular user OTP verification
        const result = await verifyOTP(otp);

        // Email shared by several accounts: let the user pick one
        if (result.requiresAccountSelection) {
          setIsLoading(false);
          setAccounts(result.accounts || []);
          return;
        }

        finishLogin();
      }
      } catch (err: any) { 
        setIsLoading(false);
        // Improved error message for invalid OTP
        const errorMessage = err.message?.toLowerCase().includes('otp') || err.message?.toLowerCase().includes('invalid')
          ? 'Invalid OTP. Please check the code and try again.'
          : err.message || 'Verification failed. Please try again.';
        
        setTimeout(() => setErrorModal({ isOpen: true, message: errorMessage }), 100);
      }
    };
    
    verifyOTPAsync();
  };

  const handleResend = async () => {
    const email = localStorage.getItem('pendingEmail');
    if (!email) return;

    const isAdmin = localStorage.getItem('isAdminLogin') === 'true';

    try {
      if (isAdmin) {
        const response = await adminAuthService.sendAdminOTP({ email });

        if (response.success && response.sessionId) {
          localStorage.setItem('adminSessionId', response.sessionId);
          setSuccessModal({ isOpen: true, message: 'Admin OTP has been resent to your email!' });
          setCountdown(30);
          setCanResend(false);
        } else {
          throw new Error('Failed to resend admin OTP');
        }
      } else {
        await login(email);
        setSuccessModal({ isOpen: true, message: 'OTP has been resent to your email!' });
        setCountdown(30);
        setCanResend(false);
      }
      setOtp('');
    } catch (err: any) {
      setErrorModal({ isOpen: true, message: err?.message || 'Failed to resend OTP. Please try again.' });
    }
  };

  const emailDisplay = localStorage.getItem('pendingOtpEmail') || localStorage.getItem('pendingEmail') || '';
  const isAdmin = localStorage.getItem('isAdminLogin') === 'true';

  return (
    <div className="ap-otp-wrapper">
      <div className="ap-otp-card">
        <div className="ap-otp-header">
          <div className="ap-otp-logo-row">
            <div className="ap-otp-logo-badge"><Gavel className="ap-otp-logo-icon" /></div>
            <div>
              <div className="ap-otp-brand-title">EasyEAuction</div>
              <div className="ap-otp-brand-sub">
                {isAdmin ? 'Admin Access - Verify identity' : 'Verify your identity to continue'}
              </div>
            </div>
          </div>
        </div>

        <div className="ap-otp-inner">
          {isAdmin && (
            <div className="ap-otp-admin-badge">
              <Shield className="ap-otp-admin-icon" />
              <span className="ap-role-badge ap-role-admin">System Administrator</span>
            </div>
          )}
          <div className="ap-otp-form-header">
            <h2 className="ap-otp-form-title"><Shield className="ap-otp-form-icon" />Verify OTP</h2>
            <p className="ap-otp-form-subtitle">
              {accounts
                ? 'This email is used by more than one account. Choose the one to log in to.'
                : <>Enter the 4-digit code sent to <strong>{emailDisplay}</strong></>}
            </p>
          </div>

          {accounts && (
            <div className="ap-otp-form-body">
              {accounts.map((account) => (
                <button
                  key={account.id}
                  type="button"
                  className="ap-otp-btn"
                  style={{ marginBottom: 12, justifyContent: 'space-between' }}
                  disabled={isLoading}
                  onClick={() => handleSelectAccount(account)}
                >
                  <span>
                    {account.company_name}
                    {account.person_name ? ` — ${account.person_name}` : ''}
                    {` (${roleLabel(account.role)})`}
                  </span>
                  <ArrowRight className="ap-otp-btn-icon" />
                </button>
              ))}
            </div>
          )}

          <div className="ap-otp-form-body" style={accounts ? { display: 'none' } : undefined}>
            <div className="ap-otp-form">
              <div className="ap-otp-form-group">
                <label htmlFor="otp" className="ap-otp-label">
                  OTP Code<span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showOtp ? 'text' : 'password'}
                    id="otp"
                    className="ap-otp-input"
                    placeholder="0000"
                    maxLength={4}
                    value={otp}
                    onChange={handleOtpChange}
                    inputMode="numeric"
                    pattern="\d{4}"
                    autoComplete="one-time-code"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (otp.length === 4 && !isLoading) {
                          onSubmit(e as any);
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowOtp((v) => !v)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280',
                    }}
                    aria-label={showOtp ? 'Hide OTP' : 'Show OTP'}
                  >
                    {showOtp ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
                <small className="ap-otp-hint">Enter exactly 4 digits</small>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSubmit(e);
                }}
                disabled={isLoading || otp.length !== 4}
                className="ap-otp-btn"
              >
                {isLoading ? (
                  <div className="ap-otp-spinner" />
                ) : (
                  <>
                    {'Verify & Login'}
                    <ArrowRight className="ap-otp-btn-icon" />
                  </>
                )}
              </button>
            </div>

            <div className="ap-otp-resend">
              {canResend ? (
                <button onClick={handleResend} className="ap-otp-btn-secondary">
                  <RefreshCw className="ap-otp-resend-icon" />
                  Resend OTP
                </button>
              ) : (
                <p className="ap-otp-countdown">Resend OTP in {countdown}s</p>
              )}
            </div>
          </div>

          <div className="ap-otp-footer">
            <p className="ap-otp-footer-text">
              Wrong email?{' '}
              <Link to="/login" className="ap-otp-footer-link">
                Change email
              </Link>
            </p>
          </div>
        </div>

        <div className="ap-otp-backhome">
          <Link to="/login" className="ap-otp-backhome-link">
            ← Back to Login
          </Link>
        </div>
      </div>

      {/* Modals */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title="Invalid OTP"
        message={errorModal.message}
      />
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ isOpen: false, message: '' })}
        title="Verification Successful"
        message={successModal.message}
      />
      <InfoModal
        isOpen={infoModal.isOpen}
        onClose={() => setInfoModal({ isOpen: false, message: '' })}
        title="Information"
        message={infoModal.message}
      />
    </div>
  );
};

export default OTPVerification;


